import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { defineStepper } from "@stepperize/react";
import { FormProvider, type FieldPath, type Resolver, useForm } from "react-hook-form";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

import Loading from "../../../../components/Loading";
import Module from "../../../../components/Module";
import Spacer from "../../../../components/Spacer";
import { Grid, Row, Col } from "../../../../components/Grid";
import { Alert, AlertDescription } from "../../../../components/ui/alert";
import { WFPButton } from "../../../../components/ui/button";
import { displayErrorMessage } from "../../../../utils";
import { ExchangeLocationForm } from "../ExchangeLocationForm";
import type {
  ExchangeLocationFormRef,
  ExchangeLocationFormValues,
} from "../ExchangeLocationForm/types";
import { RepresentativeForm } from "../RepresentativeForm";
import { DeleteRepresentativeDialog } from "./Components/DeleteRepresentativeDialog";
import {
  createTraderDetailsStepOneSchema,
  TraderDetailsStepOne,
  type TraderDetailsStepOneFormValues,
} from "./Components/TraderDetailsStepOne";
import { TraderRegistrationStepNavigation } from "./Components/TraderRegistrationStepNavigation";
import { TraderRepresentativesTable } from "./Components/TraderRepresentativesTable";
import {
  STEP_IDS,
  type EntityId,
  type StepId,
  type TraderDetailsFormProps,
  type TraderDetailsFormValues,
  type TranslationFunction,
} from "./Components/types";

export type { TraderDetailsFormProps } from "./Components/types";

type TraderDetailsFormContentProps = TraderDetailsFormProps & {
  t: TranslationFunction;
};

const traderRegistrationStepper = defineStepper([
  {
    id: STEP_IDS.DETAILS,
    titleKey: "traderDetails.form.trader",
  },
  {
    id: STEP_IDS.REPRESENTATIVES,
    titleKey: "traderDetails.form.addTraderRepresentatives",
  },
  {
    id: STEP_IDS.EXCHANGE_LOCATIONS,
    titleKey: "traderDetails.form.addExchangeLocations",
  },
] as const);

const DETAILS_STEP_FIELDS: FieldPath<TraderDetailsStepOneFormValues>[] = [
  "name",
  "trader_type",
  "location",
  "language",
];

const hasEntityId = (id: EntityId | undefined): id is EntityId =>
  id !== undefined && id !== null && id !== "";

const getInitialStepId = (
  initialStep: number | undefined,
  hasTraderId: boolean,
  hasCustomExchangeLocationFeatureEnabled: boolean
): StepId => {
  if (initialStep === 2 && hasTraderId && hasCustomExchangeLocationFeatureEnabled) {
    return STEP_IDS.EXCHANGE_LOCATIONS;
  }

  if (initialStep === 1 && hasTraderId) {
    return STEP_IDS.REPRESENTATIVES;
  }

  return STEP_IDS.DETAILS;
};

const TraderDetailsFormContent = ({
  id,
  fieldErrors = {},
  isSavingProfile = false,
  isFetchingTraderRepresentatives = false,
  onClickValidateAndClose,
  onClickValidateAndContinue,
  traderRepresentatives = [],
  onClickEditRepresentative,
  onClickDeleteRepresentative,
  isDeleteRepresentativeModalOpen,
  onClickConfirmDeleteRepresentative,
  onClickCancelDeleteRepresentative,
  currentToDeleteRepresentative = null,
  isFetchingDeleteRepresentative,
  errorMessageDeleteRepresentative,
  administrativeAreas = [],
  aggregatorOptions = [],
  isFetchingAggregators = false,
  initialStep,
  t,
  initialValues = {},
  exchangeLocations,
  exchangeLocationTypes = [],
  initialFormValuesRepresentative = {},
  isFetchingRepresentative = false,
  errorMessageRepresentative = "",
  onClickValidateRepresentativeForm,
  fieldErrorsRepresentative = {},
  onClickBackRepresentativeForm,
  isOnEditRepresentative,
  onClickValidateExchangeLocationsForm,
  isFetchingExchangeLocations = false,
  errorMessageExchangeLocations = "",
  hasCustomExchangeLocationFeatureEnabled,
}: TraderDetailsFormContentProps) => {
  const exchangeLocationFormRef = useRef<ExchangeLocationFormRef | null>(null);
  const handledInitialStepRef = useRef<string | null>(null);
  const [showBlankFieldErrors, setShowBlankFieldErrors] = useState(false);
  const [representativeWarning, setRepresentativeWarning] = useState("");
  const [isCreatingRepresentative, setIsCreatingRepresentative] = useState(false);
  const previousRepresentativesCountRef = useRef(traderRepresentatives.length);

  const traderDetailsStepOneSchema = useMemo(
    () => createTraderDetailsStepOneSchema(t("traderDetails.form.mandatoryFieldBlankError")),
    [t]
  );

  const methods = useForm<TraderDetailsFormValues>({
    defaultValues: initialValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(traderDetailsStepOneSchema) as Resolver<TraderDetailsFormValues>,
  });
  const {
    formState: { isSubmitting },
    getValues,
    handleSubmit,
    trigger,
    watch,
  } = methods;

  const hasTraderId = hasEntityId(id);
  const hasRepresentatives = traderRepresentatives.length > 0;
  const hasExchangeLocationAssignments =
    (initialValues.available_exchange_locations?.length ?? 0) > 0;

  const completedSteps = useMemo<StepId[]>(
    () => [
      ...(hasTraderId ? [STEP_IDS.DETAILS] : []),
      ...(hasRepresentatives ? [STEP_IDS.REPRESENTATIVES] : []),
      ...(hasCustomExchangeLocationFeatureEnabled && hasExchangeLocationAssignments
        ? [STEP_IDS.EXCHANGE_LOCATIONS]
        : []),
    ],
    [
      hasCustomExchangeLocationFeatureEnabled,
      hasExchangeLocationAssignments,
      hasRepresentatives,
      hasTraderId,
    ]
  );

  const availableSteps = useMemo(
    () =>
      traderRegistrationStepper.steps.filter(
        stepDefinition =>
          hasCustomExchangeLocationFeatureEnabled ||
          stepDefinition.id !== STEP_IDS.EXCHANGE_LOCATIONS
      ),
    [hasCustomExchangeLocationFeatureEnabled]
  );

  const requestedInitialStepId = useMemo(
    () => getInitialStepId(initialStep, hasTraderId, hasCustomExchangeLocationFeatureEnabled),
    [hasCustomExchangeLocationFeatureEnabled, hasTraderId, initialStep]
  );

  const stepper = traderRegistrationStepper.useStepper(
    useMemo(
      () => ({
        completed: completedSteps,
        defaultStep: requestedInitialStepId,
        linear: true,
      }),
      [completedSteps, requestedInitialStepId]
    )
  );

  const goToStep = stepper.goTo;
  const canGoToStep = stepper.canGoTo;
  const isStepperBusy = isSavingProfile || isSubmitting || stepper.isPending;
  const isProfileStepSaving = stepper.is(STEP_IDS.DETAILS) && (isSavingProfile || isSubmitting);
  const activeStepId = stepper.id;
  const isExchangeLocationsStepActive = stepper.is(STEP_IDS.EXCHANGE_LOCATIONS);
  const lastAvailableStep = availableSteps[availableSteps.length - 1];
  const isLastAvailableStep = lastAvailableStep?.id === stepper.id;

  const nameValue = watch("name");
  const languageValue = watch("language");
  const locationValue = watch("location");

  useEffect(() => {
    if (!hasCustomExchangeLocationFeatureEnabled && isExchangeLocationsStepActive) {
      void goToStep(STEP_IDS.REPRESENTATIVES);
    }
  }, [goToStep, hasCustomExchangeLocationFeatureEnabled, isExchangeLocationsStepActive]);

  useEffect(() => {
    if (initialStep === undefined || !hasTraderId) {
      return;
    }

    const initialStepKey = `${id}-${initialStep}-${hasCustomExchangeLocationFeatureEnabled}`;
    if (handledInitialStepRef.current === initialStepKey) {
      return;
    }

    handledInitialStepRef.current = initialStepKey;

    if (activeStepId !== requestedInitialStepId && canGoToStep(requestedInitialStepId)) {
      void goToStep(requestedInitialStepId);
    }
  }, [
    activeStepId,
    canGoToStep,
    goToStep,
    hasCustomExchangeLocationFeatureEnabled,
    hasTraderId,
    id,
    initialStep,
    requestedInitialStepId,
  ]);

  useEffect(() => {
    if (showBlankFieldErrors) {
      void trigger(DETAILS_STEP_FIELDS);
    }
  }, [languageValue, locationValue, nameValue, showBlankFieldErrors, trigger]);

  useEffect(() => {
    if (traderRepresentatives.length > previousRepresentativesCountRef.current) {
      setIsCreatingRepresentative(false);
    }
    previousRepresentativesCountRef.current = traderRepresentatives.length;
  }, [traderRepresentatives.length]);

  useEffect(() => {
    if (isOnEditRepresentative) {
      setIsCreatingRepresentative(false);
    }
  }, [isOnEditRepresentative]);

  const isRepresentativeFormVisible =
    !hasRepresentatives || isOnEditRepresentative || isCreatingRepresentative;
  const showCreateAnotherRepresentativeButton =
    stepper.is(STEP_IDS.REPRESENTATIVES) && hasRepresentatives && !isRepresentativeFormVisible;

  const deleteRepresentativeMessage = currentToDeleteRepresentative?.person?.full_name
    ? `${t("traderDetails.form.deleteRepresentativeMessage")} ${
        currentToDeleteRepresentative.person.full_name
      }?`
    : "";

  const nextButtonLabel = () => (isLastAvailableStep ? t("common.save") : t("common.saveContinue"));

  const submitCurrentStep = handleSubmit(async formValues => {
    if (stepper.is(STEP_IDS.DETAILS)) {
      await onClickValidateAndContinue(formValues, 0);
      return;
    }

    if (stepper.is(STEP_IDS.REPRESENTATIVES) && hasCustomExchangeLocationFeatureEnabled) {
      await onClickValidateAndContinue(formValues, 1);
      return;
    }

    await onClickValidateAndClose(formValues);
  });

  const validateDetailsStep = async () => {
    setShowBlankFieldErrors(true);
    return trigger(DETAILS_STEP_FIELDS);
  };

  const handleExchangeLocationsValidate = async (
    exchangeLocationValues: ExchangeLocationFormValues
  ) => {
    await onClickValidateExchangeLocationsForm({
      ...initialValues,
      ...getValues(),
      ...exchangeLocationValues,
    });
  };

  const handleStepSelection = async (stepId: StepId) => {
    if (isStepperBusy || stepper.id === stepId || !stepper.canGoTo(stepId)) {
      return;
    }

    if (stepId !== STEP_IDS.DETAILS && !hasTraderId) {
      return;
    }

    if (stepId === STEP_IDS.EXCHANGE_LOCATIONS && !hasCustomExchangeLocationFeatureEnabled) {
      return;
    }

    if (
      !stepper.is(STEP_IDS.DETAILS) &&
      stepId === STEP_IDS.EXCHANGE_LOCATIONS &&
      !hasRepresentatives
    ) {
      setRepresentativeWarning(t("traderDetails.form.traderRepresentativeWarning"));
      return;
    }

    if (stepper.is(STEP_IDS.DETAILS) && stepId !== STEP_IDS.DETAILS) {
      const isDetailsStepValid = await validateDetailsStep();

      if (!isDetailsStepValid) {
        return;
      }
    }

    await stepper.goTo(stepId);
  };

  const next = async () => {
    if (isStepperBusy) {
      return;
    }

    if (stepper.is(STEP_IDS.DETAILS)) {
      const isDetailsStepValid = await validateDetailsStep();

      if (!isDetailsStepValid) {
        return;
      }

      await submitCurrentStep();
      return;
    }

    if (stepper.is(STEP_IDS.REPRESENTATIVES)) {
      if (hasRepresentatives) {
        setRepresentativeWarning("");
        await submitCurrentStep();
        return;
      }

      setRepresentativeWarning(t("traderDetails.form.traderRepresentativeWarning"));
      return;
    }

    exchangeLocationFormRef.current?.submitExchangeLocationsForm();
  };

  const getStepTitle = () => {
    if (stepper.is(STEP_IDS.DETAILS)) {
      return `${t("traderDetails.form.trader")} ${t("traderDetails.form.details").toLowerCase()}`;
    }

    return t(stepper.current.titleKey);
  };

  const isStepDisabled = (stepId: StepId) => {
    if (stepId !== STEP_IDS.DETAILS && !hasTraderId) {
      return true;
    }

    if (stepId === STEP_IDS.EXCHANGE_LOCATIONS && !hasCustomExchangeLocationFeatureEnabled) {
      return true;
    }

    return !stepper.canGoTo(stepId);
  };

  return (
    <>
      <Grid>
        <Row>
          <Col sm={2}>{null}</Col>
        </Row>
        <Row>
          <Col sm={2}>
            <TraderRegistrationStepNavigation
              steps={availableSteps}
              activeStepId={stepper.id}
              isBusy={isStepperBusy}
              label={t("traderDetails.title")}
              isStepComplete={stepId => stepper.isComplete(stepId)}
              isStepDisabled={isStepDisabled}
              onStepSelect={handleStepSelection}
              t={t}
            />
          </Col>
          <Col sm={10}>
            <Module
              title={getStepTitle()}
              footer={
                <div className="flex w-full justify-end gap-3">
                  {showCreateAnotherRepresentativeButton ? (
                    <WFPButton
                      type="button"
                      variant="secondary"
                      disabled={isStepperBusy}
                      onClick={() => setIsCreatingRepresentative(true)}
                    >
                      {t("traderDetails.form.createAnotherRepresentative")}
                    </WFPButton>
                  ) : null}
                  <WFPButton type="button" disabled={isStepperBusy} onClick={next}>
                    {nextButtonLabel()}
                  </WFPButton>
                </div>
              }
            >
              <FormProvider {...methods}>
                <form
                  onSubmit={(event: FormEvent<HTMLFormElement>) => {
                    event.preventDefault();
                    void next();
                  }}
                >
                  {stepper.is(STEP_IDS.DETAILS) && (
                    <TraderDetailsStepOne
                      methods={methods}
                      showBlankFieldErrors={showBlankFieldErrors}
                      isSaving={isProfileStepSaving}
                      administrativeAreas={administrativeAreas}
                      fieldErrors={fieldErrors}
                      aggregatorOptions={aggregatorOptions}
                      isFetchingAggregators={isFetchingAggregators}
                    />
                  )}
                </form>
              </FormProvider>
              {stepper.is(STEP_IDS.REPRESENTATIVES) && (
                <Grid>
                  <Row>
                    <Col sm={12}>
                      <Alert variant="info" className="my-4">
                        <Info />
                        <AlertDescription className="whitespace-pre-line">
                          {t("traderDetails.form.representativeInstructionText", {
                            location: window.location.origin,
                          })}
                        </AlertDescription>
                      </Alert>
                    </Col>
                  </Row>
                  {isRepresentativeFormVisible && (
                    <Row>
                      <Col sm={12}>
                        <RepresentativeForm
                          initialValues={initialFormValuesRepresentative}
                          isFetching={isFetchingRepresentative}
                          errorMessage={errorMessageRepresentative}
                          onClickValidate={onClickValidateRepresentativeForm}
                          fieldErrors={fieldErrorsRepresentative}
                          onClickClose={onClickBackRepresentativeForm}
                          isOnEdit={isOnEditRepresentative}
                        />
                        <Spacer height="1rem" />
                      </Col>
                    </Row>
                  )}
                  <Row>
                    <Col sm={12}>
                      <TraderRepresentativesTable
                        representatives={traderRepresentatives}
                        isLoading={isFetchingTraderRepresentatives}
                        onClickEditRepresentative={onClickEditRepresentative}
                        onClickDeleteRepresentative={onClickDeleteRepresentative}
                        t={t}
                        warningMessage={representativeWarning}
                      />
                    </Col>
                  </Row>
                </Grid>
              )}
              {stepper.is(STEP_IDS.EXCHANGE_LOCATIONS) && (
                <Grid>
                  <Row>
                    <Col sm={12}>
                      <Alert variant="info" className="my-4">
                        <Info />
                        <AlertDescription className="whitespace-pre-line">
                          {t("traderDetails.form.exchangeLocationsInstructionText", {
                            location: window.location.origin,
                          })}
                        </AlertDescription>
                      </Alert>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <ExchangeLocationForm
                        ref={exchangeLocationFormRef}
                        initialValues={initialValues}
                        isFetching={isFetchingExchangeLocations}
                        errorMessage={errorMessageExchangeLocations}
                        onClickValidate={handleExchangeLocationsValidate}
                        administrativeAreas={administrativeAreas}
                        exchangeLocationTypes={exchangeLocationTypes}
                        exchangeLocations={exchangeLocations}
                        t={t}
                      />
                      <Spacer height="1rem" />
                    </Col>
                  </Row>
                </Grid>
              )}
            </Module>
          </Col>
        </Row>
      </Grid>
      <DeleteRepresentativeDialog
        isOpen={isDeleteRepresentativeModalOpen}
        onValidate={onClickConfirmDeleteRepresentative}
        onCancel={onClickCancelDeleteRepresentative}
        errorMessage={errorMessageDeleteRepresentative}
        isLoading={isFetchingDeleteRepresentative}
        title={t("traderDetails.form.deleteRepresentativeModalTitle")}
        text={deleteRepresentativeMessage}
      />
    </>
  );
};

export function Form(props: TraderDetailsFormProps) {
  const { isFetching, errorMessage = "" } = props;
  const { t } = useTranslation();
  const translate: TranslationFunction = (key, options) => t(key, options);

  if (isFetching) {
    return <Loading isVisible />;
  }

  if (errorMessage) {
    return <>{displayErrorMessage(errorMessage)}</>;
  }

  return <TraderDetailsFormContent {...props} errorMessage={errorMessage} t={translate} />;
}

export default Form;
